import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Avatar, Button, Empty, Select, Tag, Timeline, Typography,
} from 'antd';
import { UserOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { camelCase, sentenceCase } from 'change-case';
import * as api from 'util/api';
import { productProperties, skuProperties } from 'config/language';
import './ActivityHistory.less';

const { Text } = Typography;

/** Pricing-manager labels. price_change must not borrow SKU or product dictionaries. */
const PRICE_CHANGE_LABELS = {
  rrp_unit: 'RRP Unit',
  rrp_pack: 'RRP Pack',
  rrp_case: 'RRP Case',
  price_on: 'On Prem',
  price_off: 'Off Prem',
  price_progressive: 'Progressive',
  price_au: 'AU',
  price_vic_sa: 'VIC/SA',
  price_qld_wa: 'QLD/WA',
  price_venue_per_litre: 'Venue/L',
  price_pint: 'Pint',
  price_half_pint: 'Half Pint',
  price_150ml: '150ml',
  price_500ml: '500ml',
  price_bar: 'O.P.',
  sell_price_inc_tax: 'Kounta sell',
  cost_price_inc_tax: 'Kounta cost',
  kounta: 'Kounta',
  shopify: 'Shopify',
  cin7: 'Cin7',
  monday: 'Monday',
  matrix: 'Matrix',
  resolve: 'Resolve',
};

const fieldLabel = (field, context) => {
  const raw = field || '';
  if (context === 'price_change') {
    return PRICE_CHANGE_LABELS[raw] || sentenceCase(raw);
  }
  const key = camelCase(raw);
  const language = context === 'product_sku' ? skuProperties : productProperties;
  return language[key]?.labels?.field || sentenceCase(raw);
};

const formatValue = (value) => {
  if (value == null || value === '') return '—';
  if (typeof value === 'string' && value.length > 240) {
    return `${value.slice(0, 240)}…`;
  }
  return String(value);
};

const actorNameOf = (event) => (
  event.actor?.name || event.user?.name || 'Unknown user'
);

const STATUS_LABEL = {
  submitted: 'Submitted',
  applied: 'Confirmed applied',
  error: 'Error',
};

const statusColor = (status) => {
  if (status === 'applied') return 'success';
  if (status === 'error') return 'error';
  return 'processing';
};

const ActivityHistory = ({
  context,
  productId,
  instanceId,
  includeSkus,
  compact,
  fetchPage,
  showActorFilter,
}) => {
  const [events, setEvents] = useState([]);
  const [nextBefore, setNextBefore] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [actorType, setActorType] = useState(undefined);

  const load = useCallback(async ({ before, append } = {}) => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        limit: 50,
        includeSkus: includeSkus ? 1 : undefined,
        actorType,
        before,
      };
      const result = fetchPage
        ? await fetchPage({ before, actorType, limit: 50 })
        : await (context === 'product_sku'
          ? api.getProductSkuActivity(productId, instanceId, params)
          : api.getProductActivity(productId, params));
      setEvents((current) => (append ? [...current, ...result.events] : result.events));
      setNextBefore(result.nextBefore);
    } catch (err) {
      setError('Unable to load change history.');
    } finally {
      setLoading(false);
    }
  }, [actorType, context, fetchPage, includeSkus, instanceId, productId]);

  useEffect(() => {
    if (!fetchPage && (!productId || (context === 'product_sku' && !instanceId))) return;
    load();
  }, [load, productId, instanceId, context, fetchPage]);

  return (
    <div className={`activity-history${compact ? ' activity-history--compact' : ''}`}>
      {showActorFilter ? (
        <div className="activity-history-filters">
          <Select
            allowClear
            placeholder="All actors"
            style={compact ? { width: '100%' } : { minWidth: 160 }}
            value={actorType}
            onChange={setActorType}
            options={[
              { value: 'user', label: 'Matrix users' },
              { value: 'monday', label: 'Monday' },
              { value: 'system', label: 'System' },
            ]}
          />
        </div>
      ) : null}
      {error && <Text type="danger">{error}</Text>}
      {!loading && events.length === 0 && !error && (
        <Empty description="No recorded changes yet" />
      )}
      <Timeline>
        {events.map((event) => (
          <Timeline.Item key={event.id}>
            <div className="activity-history-event">
              <div className="activity-history-event-head">
                <Avatar
                  size="small"
                  icon={<UserOutlined />}
                  src={event.actor?.photoUrl || event.user?.profilePhotoUrl}
                />
                <Text strong>{actorNameOf(event)}</Text>
                {event.context === 'product_sku' && includeSkus && (
                  <Tag color="blue">{event.instanceName || `SKU ${event.instanceId}`}</Tag>
                )}
                {event.context === 'price_change' && (
                  <Tag>{event.instanceName || `SKU ${event.instanceId}`}</Tag>
                )}
                <Text type="secondary">
                  {dayjs(event.timestamp).format('D MMM YYYY, h:mm a')}
                </Text>
              </div>
              {event.context === 'price_change' ? (
                <div>
                  <Tag>{event.platform || 'platform'}</Tag>
                  <Tag color={statusColor(event.status)}>
                    {STATUS_LABEL[event.status] || event.status || 'Submitted'}
                  </Tag>
                  {event.errorDetail ? (
                    <Text type={event.status === 'error' ? 'danger' : 'secondary'}>
                      {event.errorDetail}
                    </Text>
                  ) : null}
                </div>
              ) : (
                <Text type="secondary">
                  {sentenceCase(event.type)}
                  {' '}
                  {event.context === 'product_sku' ? 'SKU' : 'product'}
                </Text>
              )}
              {(event.changes || []).length > 0 && (
                <ul className="activity-history-changes">
                  {event.changes.map((change) => (
                    <li key={`${event.id}-${change.field}`}>
                      <Text strong>
                        {fieldLabel(change.field, event.context)}
                      </Text>
                      {': '}
                      <span className="activity-history-prev">
                        {formatValue(change.previousValue)}
                      </span>
                      {' → '}
                      <span className="activity-history-next">
                        {formatValue(change.newValue)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </Timeline.Item>
        ))}
      </Timeline>
      {nextBefore && (
        <Button
          loading={loading}
          onClick={() => load({ before: nextBefore, append: true })}
        >
          Load more
        </Button>
      )}
    </div>
  );
};

ActivityHistory.propTypes = {
  context: PropTypes.oneOf(['product', 'product_sku', 'price_change']),
  productId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  instanceId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  includeSkus: PropTypes.bool,
  compact: PropTypes.bool,
  fetchPage: PropTypes.func,
  showActorFilter: PropTypes.bool,
};

ActivityHistory.defaultProps = {
  context: 'product',
  productId: null,
  instanceId: null,
  includeSkus: false,
  compact: false,
  fetchPage: null,
  showActorFilter: true,
};

export default ActivityHistory;
